import {GroupedEventList} from '@/components/groupedEventList/groupedEventList';
import {PaginationGroup} from '@/components/paginationGroup/paginationGroup';
import {Button} from '@/components/ui/button';
import {Pill} from '@/components/ui/pill';
import {Typography} from '@/components/ui/typography';
import {getEventListById} from '@/server/savedEvent';
import {Link, createFileRoute, useRouteContext} from '@tanstack/react-router';
import {ArrowLeft, ExternalLink, Pencil} from 'lucide-react';
import {z} from 'zod';

const listSearchSchema = z.object({
  page: z.number().int().positive().catch(1),
  pageSize: z.number().int().positive().max(50).catch(12),
});

export const Route = createFileRoute('/dashboard/lists/$listId/')({
  validateSearch: (search) => listSearchSchema.parse(search),
  loaderDeps: ({search}) => ({page: search.page, pageSize: search.pageSize}),
  loader: ({params, deps}) =>
    getEventListById({
      data: {
        listId: params.listId,
        page: deps.page,
        pageSize: deps.pageSize,
      },
    }),
  errorComponent: ListNotFound,
  component: ListDetailPage,
});

/**
 * Detail view for a single event list.
 * Shows paginated event cards with empty state.
 */
function ListDetailPage() {
  const {list, events, pagination} = Route.useLoaderData();
  const {profile} = useRouteContext({from: '__root__'});

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-3 items-center">
          <Link to="/dashboard/lists">
            <Button variant="ghost" size="icon" aria-label="Back to lists">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <Typography level="H3">{list.name}</Typography>
          {list.kind === 'SMART' && (
            <Pill className="bg-neutral-200 text-neutral-700">Smart</Pill>
          )}
        </div>
        <div className="flex gap-2">
          {list.id !== 'saved' && (
            <Link to="/dashboard/lists/$listId/edit" params={{listId: list.id}}>
              <Button
                variant="outline"
                size="icon"
                className="md:w-auto md:px-3 md:gap-1.5"
              >
                <Pencil className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Edit</span>
              </Button>
            </Link>
          )}
          {list.visibility === 'PUBLIC' && profile && (
            <Link
              to="/rider/$profileId/list/$listId"
              params={{profileId: profile.id, listId: list.id}}
              search={{page: 1, pageSize: 12}}
            >
              <Button
                variant="outline"
                size="icon"
                className="md:w-auto md:px-3 md:gap-1.5"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span className="hidden md:inline">View public page</span>
              </Button>
            </Link>
          )}
        </div>
      </div>

      {events.length === 0 ? (
        <div className="p-8 text-center">
          <Typography level="B1" className="text-neutral-500 mb-4">
            No events in this list yet.
          </Typography>
          <Link to="/search">
            <Button variant="outline">Browse Events</Button>
          </Link>
        </div>
      ) : (
        <>
          <GroupedEventList events={events} />

          {pagination.totalPages > 1 && (
            <PaginationGroup pagination={pagination} />
          )}
        </>
      )}
    </div>
  );
}

/** Shown when getEventListById throws (invalid list ID). */
function ListNotFound() {
  return (
    <div className="flex flex-col items-center gap-4 py-12">
      <Typography level="H3" className="text-slate-900">
        List not found
      </Typography>
      <Typography level="B1" className="text-slate-500">
        This list doesn&apos;t exist or you don&apos;t have access to it.
      </Typography>
      <Link to="/dashboard/lists">
        <Button variant="outline">Back to Lists</Button>
      </Link>
    </div>
  );
}
